import gsap from "gsap";

const DESKTOP_BREAKPOINT = 1189.98;
const PANEL_EASE = "power3.inOut";

export default class SiteMenu {
	constructor(app) {
		this.className = "c-site-menu";
		this.tl = null;
		this.app = app;
		this.lastScroll = -10;
		this.isOpen = false;
		this.DOM = {};
		this.initDomElements();
		this.initEvents();
	}

	initDomElements() {
		this.$el = document.querySelector("." + this.className);
		if (!this.$el) return;

		this.DOM.burger = this.$el.querySelector("." + this.className + "__burger");
		this.DOM.nav = document.querySelector("." + this.className + "__nav");
		this.refreshNavElements();
		this.syncNavPlacement();
		this.resetPanelState();
	}

	refreshNavElements() {
		if (!this.DOM.nav) return;

		this.DOM.panel = this.DOM.nav.querySelector("." + this.className + "__nav-panel");
		this.DOM.submenuToggles = this.DOM.nav.querySelectorAll("." + this.className + "__nav-toggle");
		this.DOM.links = this.DOM.nav.querySelectorAll(
			"." +
				this.className +
				"__nav-link, ." +
				this.className +
				"__submenu-link, ." +
				this.className +
				"__nav-toggle, ." +
				this.className +
				"__nav-cta, ." +
				this.className +
				"__nav__social li, ." +
				this.className +
				"__nav__social span"
		);
	}

	syncNavPlacement() {
		if (!this.DOM.nav) return;

		if (this.isMobileMenu()) {
			if (!this.DOM.nav.dataset.teleported) {
				this.navPlaceholder = document.createComment("site-menu-nav");
				this.DOM.nav.parentNode.insertBefore(this.navPlaceholder, this.DOM.nav);
				document.body.appendChild(this.DOM.nav);
				this.DOM.nav.dataset.teleported = "true";
			}
			return;
		}

		this.restoreNavPlacement();
	}

	restoreNavPlacement() {
		if (!this.DOM.nav?.dataset.teleported || !this.navPlaceholder?.parentNode) return;

		this.navPlaceholder.parentNode.insertBefore(this.DOM.nav, this.navPlaceholder);
		this.navPlaceholder.remove();
		this.navPlaceholder = null;
		delete this.DOM.nav.dataset.teleported;
		this.refreshNavElements();
	}

	resetPanelState() {
		if (!this.DOM.nav) return;

		this.tl?.kill();
		gsap.killTweensOf([this.DOM.nav, ...(this.DOM.links || [])]);
		this.isOpen = false;
		this.$el?.classList.remove("opened");
		this.DOM.burger?.classList.remove("active");
		this.DOM.burger?.setAttribute("aria-expanded", "false");
		this.DOM.burger?.setAttribute("aria-label", "Ouvrir le menu");
		this.DOM.nav.classList.remove("is-active");
		this.closeSubmenus();

		if (this.isMobileMenu()) {
			gsap.set(this.DOM.nav, { clearProps: "transform" });
			gsap.set(this.DOM.links, { clearProps: "opacity,visibility,transform" });
			gsap.set(this.DOM.nav, { visibility: "hidden", x: "100%" });
			this.DOM.nav.setAttribute("aria-hidden", "true");
			return;
		}

		gsap.set(this.DOM.nav, { clearProps: "all" });
		gsap.set(this.DOM.links, { clearProps: "all" });
		this.DOM.nav.setAttribute("aria-hidden", "false");
	}

	isMobileMenu() {
		return window.matchMedia(`(max-width:${DESKTOP_BREAKPOINT}px)`).matches;
	}

	closeSubmenus() {
		this.DOM.nav?.querySelectorAll("." + this.className + "__nav-item.is-open").forEach((item) => {
			item.classList.remove("is-open");
			item.querySelector("." + this.className + "__nav-toggle")?.setAttribute("aria-expanded", "false");
		});
	}

	toggleSubmenu(toggle) {
		if (!this.isMobileMenu()) return;

		const item = toggle.closest("." + this.className + "__nav-item");
		if (!item) return;

		const isOpen = item.classList.contains("is-open");

		this.DOM.nav.querySelectorAll("." + this.className + "__nav-item.is-open").forEach((openItem) => {
			if (openItem !== item) {
				openItem.classList.remove("is-open");
				openItem.querySelector("." + this.className + "__nav-toggle")?.setAttribute("aria-expanded", "false");
			}
		});

		item.classList.toggle("is-open", !isOpen);
		toggle.setAttribute("aria-expanded", String(!isOpen));
	}

	closeMenu() {
		if (!this.isMobileMenu()) {
			document.querySelectorAll("a").forEach((item) => item.blur());
			return;
		}

		if (this.isOpen) {
			this.toggleMenu(false);
		}
	}

	toggleMenu(open) {
		if (!this.isMobileMenu() || !this.DOM.burger || !this.DOM.nav) return;

		const shouldOpen = open ?? !this.isOpen;
		if (shouldOpen === this.isOpen) return;

		this.isOpen = shouldOpen;
		this.tl?.kill();
		gsap.killTweensOf([this.DOM.nav, ...this.DOM.links]);

		if (!shouldOpen) {
			this.closeSubmenus();
		}

		this.DOM.burger.classList.toggle("active", shouldOpen);
		this.$el.classList.toggle("opened", shouldOpen);
		this.DOM.nav.classList.toggle("is-active", shouldOpen);
		this.DOM.burger.setAttribute("aria-expanded", String(shouldOpen));
		this.DOM.burger.setAttribute("aria-label", shouldOpen ? "Fermer le menu" : "Ouvrir le menu");
		this.DOM.nav.setAttribute("aria-hidden", String(!shouldOpen));

		this.tl = gsap.timeline();

		if (shouldOpen) {
			this.app.emit("disable-scroll");
			gsap.set(this.DOM.links, { autoAlpha: 0, y: 24 });
			this.tl.set(this.DOM.nav, { visibility: "visible", x: "100%" });
			this.tl.to(this.DOM.nav, { x: 0, duration: 0.65, ease: PANEL_EASE });
			this.tl.to(
				this.DOM.links,
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.55,
					stagger: 0.06,
					ease: "power3.out",
					clearProps: "transform",
				},
				"-=0.35"
			);
		} else {
			this.tl.to(this.DOM.links, {
				autoAlpha: 0,
				y: 12,
				duration: 0.2,
				stagger: { each: 0.03, from: "end" },
				ease: "power2.in",
			});
			this.tl.to(this.DOM.nav, { x: "100%", duration: 0.5, ease: PANEL_EASE });
			this.tl.set(this.DOM.nav, { visibility: "hidden", x: "100%" });
			this.tl.add(() => {
				gsap.set(this.DOM.links, { clearProps: "opacity,visibility,transform" });
				this.app.emit("enable-scroll");
			});
		}
	}

	initEvents() {
		if (!this.$el) return;

		this.app.on("close-menu", () => this.closeMenu());

		this.DOM.burger?.addEventListener("click", () => this.toggleMenu());

		this.DOM.nav?.addEventListener("click", (event) => {
			const toggle = event.target.closest("." + this.className + "__nav-toggle");
			if (!toggle) return;

			event.preventDefault();
			event.stopPropagation();
			this.toggleSubmenu(toggle);
		});

		this.DOM.nav?.querySelectorAll("a[href]").forEach((link) => {
			link.addEventListener("click", () => this.closeMenu());
		});

		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape") this.closeMenu();
		});

		this.mobileMedia = window.matchMedia(`(max-width:${DESKTOP_BREAKPOINT}px)`);
		this.onMobileMediaChange = () => {
			this.resetPanelState();
			this.syncNavPlacement();
		};
		this.mobileMedia.addEventListener("change", this.onMobileMediaChange);

		this.app.on("scroll", this.handleScroll.bind(this));
	}

	handleScroll() {
		if (window.scrollY > this.lastScroll) {
			this.$el.classList.remove("--scrolltop");
		} else if (window.scrollY < this.lastScroll) {
			this.$el.classList.add("--scrolltop");
		}

		if (window.scrollY > 150) {
			this.$el.classList.add("--scrolling");
		} else {
			this.$el.classList.remove("--scrolling", "bg-white");
		}

		this.lastScroll = window.scrollY;
	}

	destroy() {
		this.tl?.kill();
		this.mobileMedia?.removeEventListener("change", this.onMobileMediaChange);
		this.resetPanelState();
		this.restoreNavPlacement();
	}
}
